import { useEffect, useState, useRef } from 'react';
import { useActionData, useSubmit, useNavigate, useLoaderData } from 'react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import Page from 'client/components/aria/Page/Page';
import Button from 'client/components/aria/Button/Button';
import MapPreview from 'client/components/MapPreview/MapPreview';
import Select from 'client/components/GDS/Select/Select';
import { MAP_PAGE, VIEW_OBSERVATION_PAGE } from 'client/utils/routes/routeConstants';
import TextArea from 'client/components/GDS/TextArea/TextArea';
import TextInput from 'client/components/GDS/TextInput/TextInput';
import { mapModes } from 'client/utils/constants/appStates';
import { removeMapMode, addMapMode } from 'client/store/mapSlice/mapSlice';
import { useDispatch, useSelector } from 'react-redux';
import FormGroup from 'client/components/GDS/FormGroup/FormGroup';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import Markdown from 'client/components/aria/MarkDown/Markdown';
import DATA_SENSITIVITY_LEVELS from 'client/utils/constants/dataSensitivityLevels';
import styles from './SaveObservationPage.module.scss';
import { bufferedPoint } from 'client/utils/helpers/Aoi';

const SCOPES = {
    PRIVATE: 'Private',
    ORGANISATION_EXTENDED: 'Organisation Extended'
};

const SaveObservationPage = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const loaderData = useLoaderData() || {};
    const { observationTypes, disclaimerContent } = loaderData;
    const actionData = useActionData() || {};
    const { geometry, featuresByAssetType, aoiContainsSensitiveFeatures } = actionData;
    const [draftObservation, setDraftObservation] = useState(actionData.draftObservation || loaderData.editingObservation || {});
    const isNewObservation = !draftObservation?.systemId?.length;
    const dispatch = useDispatch();
    const currentLocale = useSelector(state => state?.locale?.current);
    const locale = useRef(currentLocale);
    const submit = useSubmit();
    const [fieldHasChanged, setFieldHasChanged] = useState({});
    const [forceValidate, setForceValidate] = useState(false);

    if (observationTypes) {
        // TODO Welsh - only english at moment
        // if (locale?.current === 'en') {
        observationTypes.sort((a, b) => a.value.en.localeCompare(b.value.en));
        // } else {
        //    observationTypes.sort((a, b) => a.value.cy.localeCompare(b.value.cy));
        // }
    }

    useEffect(() => {
        // Kick user out if navigating here directly
        // Unlike view page, we have to navigate here instead of action/loader, because draft could be defined in either.
        if (!draftObservation?.geometry) {
            navigate(MAP_PAGE);
        }
    }, [draftObservation]);

    useEffect(() => {
        // Set the scope depending on whether AOI contains sensitive assets
        setDraftObservation({
            potentialSensitivity: aoiContainsSensitiveFeatures,
            ...draftObservation, // Overides potentially sensitiviy if present
            scopeOfSharing: aoiContainsSensitiveFeatures ? SCOPES.PRIVATE : SCOPES.ORGANISATION_EXTENDED,
            dataSensitivityLevel: DATA_SENSITIVITY_LEVELS.NO_ADDITIONAL_REQUIREMENTS
        });
    }, [aoiContainsSensitiveFeatures]);

    useEffect(() => {
        if (locale?.current !== currentLocale && draftObservation) {
            // Reload page when locale changed, to trigger CMS content re-fetch
            const formData = new FormData();
            formData.append('geometry', JSON.stringify(geometry));
            formData.append('draftObservation', JSON.stringify(draftObservation));
            submit(formData, {
                action: '',
                method: 'POST'
            });
        }
    }, [currentLocale]);

    if (!draftObservation?.geometry) {
        return <></>;
    }

    const aoi = actionData.aoi ? actionData.aoi : bufferedPoint(draftObservation.geometry.coordinates);
    const INITIAL_CATEGORY = 'INITIAL_CATEGORY';
    const descriptionMaxLength = 500;

    const formValidators = {
        objectName: (val) => val?.length > 0,
        observationCategory: (val) => {
            return val && val !== INITIAL_CATEGORY;
        },
        notes: (val) => val?.length > 0 && val.length <= descriptionMaxLength
    };

    const getCategoryOption = (observationType) => {
        const val = observationType.value[currentLocale];
        return <option key={val} value={val}>{val}</option>;
    };

    const submitTo = (action, formKey) => {
        const formData = new FormData();
        formData.append(formKey, JSON.stringify(draftObservation));
        submit(formData, {
            action,
            method: 'POST'
        });
    };

    const formIsValid = () => {
        return Object.entries(formValidators).every(([requiredField, validator]) => {
            return validator(draftObservation[requiredField]);
        });
    };

    const handleSubmit = () => {
        if (!formIsValid()) {
            setForceValidate(true);
            return;
        }
        submitTo('', 'submittedObservation');
    };

    const handleEdit = () => {
        dispatch(addMapMode(mapModes.ADDING_OBSERVATION));
        submitTo(MAP_PAGE, 'draftObservation');
    };

    const handleFieldChange = (e) => {
        const name = e.target.name;
        const value = e.target.value;

        setFieldHasChanged({
            ...fieldHasChanged,
            [name]: true
        });

        setDraftObservation({
            ...draftObservation,
            [name]: value
        });
    };

    const handleCancel = () => {
        dispatch(removeMapMode(mapModes.ADDING_OBSERVATION));
        if (draftObservation?.systemId) {
            navigate(VIEW_OBSERVATION_PAGE + '/' + draftObservation.systemId);
        } else {
            navigate(MAP_PAGE);
        }
    };

    const getErrorMessage = (fieldName, errorIntlKey) => {
        const fieldValue = draftObservation[fieldName];
        const shouldDisplayErrorIfInvalid = (forceValidate || fieldHasChanged[fieldName]);
        if (shouldDisplayErrorIfInvalid && !formValidators[fieldName](fieldValue)) {
            return intl.formatMessage({ id: errorIntlKey });
        }
        return '';
    };

    // Show the observation they are adding in the minimap
    // by overwriting the existing observations to contain just the location of the draft one.
    // this also has the benefit of hiding existing observations on the minimap, decluttering for clarity
    const featuresWithDraftObservation = {
        ...featuresByAssetType,
        observation: [new Feature({
            geometry: new Point(draftObservation.geometry.coordinates)
        })]
    };

    const title = intl.formatMessage({ id: isNewObservation ? 'saveObservationPage.newTitle' : 'saveObservationPage.editTitle' });

    return <Page title={title} data-testid="saveObservationPage">
        <div className="govuk-grid-row">
            <div className="govuk-grid-column-full">
                <h1 className='govuk-heading-xl govuk-!-margin-bottom-2'>
                    {title}
                </h1>
            </div>
        </div>
        <div className="govuk-grid-row">
            <div className="govuk-grid-column-two-thirds">
                <TextInput
                    autoComplete='off'
                    label={intl.formatMessage({ id: 'saveObservationPage.displayTitle' })}
                    description={intl.formatMessage({ id: 'saveObservationPage.titleDescription' })}
                    name='objectName'
                    errorMessage={getErrorMessage('objectName', 'saveObservationPage.cannotBeEmpty')}
                    data-testid='saveObservationPage.objectName'
                    onChange={handleFieldChange}
                    value={draftObservation.objectName || ''}
                />

                <Select
                    label={intl.formatMessage({ id: 'saveObservationPage.observationCategory' })}
                    name='observationCategory'
                    data-testid='saveObservationPage.observationCategory'
                    onChange={handleFieldChange}
                    errorMessage={getErrorMessage('observationCategory', 'saveObservationPage.chooseCategory')}
                    value={draftObservation.observationCategory || INITIAL_CATEGORY}
                >
                    <option value={INITIAL_CATEGORY}>{intl.formatMessage({ id: 'saveObservationPage.chooseCategory' })}</option>
                    {observationTypes.map(getCategoryOption)}
                </Select>

                <TextArea
                    label={intl.formatMessage({ id: 'saveObservationPage.description' })}
                    description={intl.formatMessage({ id: 'saveObservationPage.descriptionDescription' })}
                    maxLength={descriptionMaxLength}
                    name='notes'
                    value={draftObservation.notes || ''}
                    onChange={handleFieldChange}
                    errorMessage={getErrorMessage('notes', draftObservation.notes?.length > descriptionMaxLength ? 'saveObservationPage.tooLong' : 'saveObservationPage.cannotBeEmpty')}
                    data-testid='saveObservationPage.notes'
                />

                <FormGroup errorMessage={forceValidate && !formIsValid() ? intl.formatMessage({ id: 'saveObservationPage.correctErrors' }) : ''}>
                    <div className="govuk-button-group govuk-!-margin-top-4">
                        <Button
                            variant="error"
                            type="button"
                            onPress={handleCancel}
                            data-testid='saveObservationPage.cancelButton'
                        >
                            {intl.formatMessage({ id: 'general.cancel' })}
                        </Button>
                        <Button
                            variant="default"
                            data-testid='saveObservationPage.submitButton'
                            onPress={handleSubmit}
                        >
                            {isNewObservation ? intl.formatMessage({ id: 'saveObservationPage.createButton.label' }) : intl.formatMessage({ id: 'saveObservationPage.saveButton.label' })}
                        </Button>
                    </div>
                </FormGroup>
            </div>
            <div className="govuk-grid-column-one-third">
                <h2 className='govuk-heading-m govuk-!-margin-bottom-4'>
                    <FormattedMessage id='saveObservationPage.mapPreview.title' />
                </h2>
                {aoi && <MapPreview data-testid='mapPreview' aoi={aoi} featuresByAssetType={featuresWithDraftObservation} />}
                <Button onPress={handleEdit} className={styles.button}>
                    {intl.formatMessage({ id: 'saveObservationPage.editButton' })}
                </Button>
                <p className="govuk-body-s">
                    <span className="govuk-!-font-weight-bold">{intl.formatMessage({ id: 'saveObservationPage.observationLocation' })}</span>
                    <span data-testid='saveObservationPage.coordinates'>{`${draftObservation.geometry.coordinates[0].toFixed(2)}, ${draftObservation.geometry.coordinates[1].toFixed(2)}`}</span>
                </p>
            </div>
        </div>
        <Markdown data-testid={'saveObservationPage.cmsContent'}>
            {disclaimerContent.markdown}
        </Markdown>
    </Page>;
};

export default SaveObservationPage;

export {
    SaveObservationPage,
    SCOPES as _SCOPES // only for jest
};
